import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Select from 'react-select';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CircularProgress from '@mui/material/CircularProgress';
import '../Customer/Customer.css';

const UserDetail = () => {
    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, logout, permissions, token } = useAuth();

    const [isSavingRoles, setIsSavingRoles] = useState(false);
    const [isSavingClaims, setIsSavingClaims] = useState(false);
    const [selectedOption, setSelectedOption] = useState({ value: '1', label: 'Master' });
    const [isEditMode, setIsEditMode] = useState(false);
    const [isClaimEditMode, setIsClaimEditMode] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [formData, setFormData] = useState({
        FirstName: '',
        LastName: '',
        UserName: '',
        Email: '',
        ContactNumber: '',
        Roles: [],
    });

    const [formDataClaim, setFormDataClaim] = useState({
        Claim: [],
    });

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    const hasEditAdministrationPermission = permissions.includes('Edit Administration');

    useEffect(() => {
        fetchData('UserApi', `/UserEdit/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    const { User } = response.response_data;
                    setFormData((prev) => ({
                        ...prev,
                        ...User,
                        Roles: response.response_data.Roles?.map((role) => ({ ...role })) || [],
                    }));
                    setFormDataClaim((prev) => ({
                        ...prev,
                        Claim: response.response_data.Claims?.map((claim) => ({ ...claim })) || [],
                    }));
                }
            })
            .catch((error) => {
                console.error('Error fetching User Details:', error);
            });
    }, [id, token]);

    const handleChangeRole = (index) => {
        const updatedRoles = formData.Roles.map((role, i) => ({
            ...role,
            IsSelected: i === index ? !role.IsSelected : false,
        }));
        setFormData((prev) => ({ ...prev, Roles: updatedRoles }));
    };

    const getClaimRange = () => {
        const startIndex = (parseInt(selectedOption.value, 10) - 1) * 5;
        const endIndex = startIndex + 4;
        return { startIndex, endIndex };
    };

    const handleDropdownChange = (newSelectedOption) => {
        setSelectedOption(newSelectedOption);
    };

    const handleChange = (index) => {
        const updatedClaims = [...formDataClaim.Claim];
        updatedClaims[index].IsSelected = !updatedClaims[index].IsSelected;
        setFormDataClaim((prev) => ({ ...prev, Claim: updatedClaims }));
    };

    const saveRoles = async () => {
        try {
            setIsSavingRoles(true);
            const response = await updateData('UserApi', `SaveUserRoles/tmp/${id}`, formData, token);
            if (response.responseCode === '1111') {
                setSuccessMessage('Roles saved successfully');
                setTimeout(() => setSuccessMessage(''), 3000);
            }
            setIsEditMode(false);
        } catch (error) {
            console.error('Error saving roles:', error);
            setErrorMessage('Failed to save roles');
        } finally {
            setIsSavingRoles(false);
        }
    };

    const saveClaims = async () => {
        try {
            setIsSavingClaims(true);
            const response = await updateData('UserApi', `SaveUserClaims/UpdateClaim/tmp/${id}`, formDataClaim, token);
            if (response.responseCode === '1111') {
                setSuccessMessage('Claims saved successfully');
                setTimeout(() => setSuccessMessage(''), 3000);
            }
            setIsClaimEditMode(false);
        } catch (error) {
            console.error('Error saving claims:', error);
            setErrorMessage('Failed to save claims');
        } finally {
            setIsSavingClaims(false);
        }
    };

    const selectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '38px',
            fontSize: '13px',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
    };

    return (
        <div className="customer-container">
            {/* Header */}
            <div className="page-header">
                <h1 className="page-title">User Details</h1>
                <div className="header-actions">
                    <Link to="/UserIndex" className="btn-back">
                        <ArrowBackIcon fontSize="small" />
                        Back to List
                    </Link>
                </div>
            </div>

            {/* Messages */}
            {errorMessage && <div className="message error">{errorMessage}</div>}
            {successMessage && <div className="message success">{successMessage}</div>}

            {/* User Info Card */}
            <div style={{ 
                background: 'white', 
                borderRadius: '8px', 
                padding: '20px', 
                marginBottom: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <PersonIcon style={{ color: '#1e3a5f', fontSize: '24px' }} />
                    <h2 style={{ margin: 0, fontSize: '18px', color: '#1e3a5f' }}>User Information</h2>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div>
                        <label style={{ fontSize: '12px', color: '#64748b', fontWeight: '500', display: 'block', marginBottom: '4px' }}>First Name</label>
                        <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>{formData.FirstName || '—'}</div>
                    </div>
                    <div>
                        <label style={{ fontSize: '12px', color: '#64748b', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Last Name</label>
                        <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>{formData.LastName || '—'}</div>
                    </div>
                    <div>
                        <label style={{ fontSize: '12px', color: '#64748b', fontWeight: '500', display: 'block', marginBottom: '4px' }}>User Name</label>
                        <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>{formData.UserName || '—'}</div>
                    </div>
                    <div>
                        <label style={{ fontSize: '12px', color: '#64748b', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Contact Number</label>
                        <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>{formData.ContactNumber || '—'}</div>
                    </div>
                    <div>
                        <label style={{ fontSize: '12px', color: '#64748b', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Email</label>
                        <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>{formData.Email || '—'}</div>
                    </div>
                </div>
            </div>

            {/* Claims and Roles Section */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                {/* User Claims Card */}
                <div style={{ 
                    background: 'white', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    <div style={{ 
                        padding: '16px 20px', 
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: '#f8fafc'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <SecurityIcon style={{ color: '#7c3aed', fontSize: '20px' }} />
                            <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>User Claims</h3>
                        </div>
                        {hasEditAdministrationPermission && (
                            <button
                                onClick={isClaimEditMode ? saveClaims : () => setIsClaimEditMode(true)}
                                disabled={isSavingClaims}
                                style={{
                                    padding: '6px 14px',
                                    background: isClaimEditMode ? '#059669' : '#7c3aed',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                {isSavingClaims ? <CircularProgress size={14} color="inherit" /> : null}
                                {isClaimEditMode ? (isSavingClaims ? 'Saving...' : 'Save Claims') : 'Manage Claims'}
                            </button>
                        )}
                    </div>
                    <div style={{ padding: '16px 20px' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <Select
                                options={[
                                    { value: '1', label: 'Master' },
                                    { value: '2', label: 'Transaction' },
                                    { value: '3', label: 'Reports' },
                                    { value: '4', label: 'Administration' },
                                ]}
                                value={selectedOption}
                                onChange={handleDropdownChange}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                            />
                        </div>
                        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                            {formDataClaim.Claim?.length > 0 ? (
                                formDataClaim.Claim.map((claim, index) => {
                                    const { startIndex, endIndex } = getClaimRange();
                                    if (index >= startIndex && index <= endIndex) {
                                        return (
                                            <div key={index} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '10px 12px',
                                                background: index % 2 === 0 ? '#f8fafc' : 'white',
                                                borderRadius: '4px',
                                                marginBottom: '4px'
                                            }}>
                                                <label style={{ fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                                                    {claim.ClaimValue}
                                                </label>
                                                <input
                                                    type="checkbox"
                                                    checked={claim.IsSelected}
                                                    onChange={() => handleChange(index)}
                                                    disabled={!isClaimEditMode}
                                                    style={{ 
                                                        width: '18px', 
                                                        height: '18px', 
                                                        cursor: isClaimEditMode ? 'pointer' : 'not-allowed',
                                                        accentColor: '#7c3aed'
                                                    }}
                                                />
                                            </div>
                                        );
                                    }
                                    return null;
                                })
                            ) : (
                                <p style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '20px' }}>No claims available.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* User Roles Card */}
                <div style={{ 
                    background: 'white', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    <div style={{ 
                        padding: '16px 20px', 
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: '#f8fafc'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <AdminPanelSettingsIcon style={{ color: '#1e3a5f', fontSize: '20px' }} />
                            <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>User Roles</h3>
                        </div>
                        {hasEditAdministrationPermission && (
                            <button
                                onClick={isEditMode ? saveRoles : () => setIsEditMode(true)}
                                disabled={isSavingRoles}
                                style={{
                                    padding: '6px 14px',
                                    background: isEditMode ? '#059669' : '#1e3a5f',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                {isSavingRoles ? <CircularProgress size={14} color="inherit" /> : null}
                                {isEditMode ? (isSavingRoles ? 'Saving...' : 'Save Roles') : 'Manage Roles'}
                            </button>
                        )}
                    </div>
                    <div style={{ padding: '16px 20px' }}>
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {formData.Roles?.length > 0 ? (
                                formData.Roles.map((role, index) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px 12px',
                                        background: index % 2 === 0 ? '#f8fafc' : 'white',
                                        borderRadius: '4px',
                                        marginBottom: '4px'
                                    }}>
                                        <label style={{ fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                                            {role.RoleName}
                                        </label>
                                        <input
                                            type="checkbox"
                                            checked={role.IsSelected}
                                            onChange={() => handleChangeRole(index)}
                                            disabled={!isEditMode}
                                            style={{ 
                                                width: '18px', 
                                                height: '18px', 
                                                cursor: isEditMode ? 'pointer' : 'not-allowed',
                                                accentColor: '#1e3a5f'
                                            }}
                                        />
                                    </div>
                                ))
                            ) : (
                                <p style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '20px' }}>No roles available.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetail;
